import { ScreenHeader } from "@/components/common/ScreenHeader";
import { LogOutModal } from "@/components/log-out-modal";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { setAppColorScheme, useColorScheme } from "@/hooks/use-color-scheme";
import { showAlert } from "@/lib/alert";
import authService from "@/lib/auth";
import {
  imagePickerPresets,
  normalizeImageAsset,
  pickImageFromLibrary,
  validateFileSize,
} from "@/lib/image-picker";
import { getRoleLabel, isDriverRole } from "@/lib/roles";
import { showToast } from "@/lib/toast";
import { checkStoreUpdate } from "@/lib/utils/check-store-update";
import { useAuthStore, useUser } from "@/stores/use-auth-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import * as Application from "expo-application";
import { router } from "expo-router";
import React, { useState } from "react";
import {
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

const isWeb = Platform.OS === "web";

export default function ProfileScreen() {
  const user = useUser();
  const { signOut, setUser, theme } = useAuth();
  const logOut = useAuthStore((state) => state.logOut);
  const isDriver = isDriverRole(user?.role);
  const colorScheme = useColorScheme();
  const isDarkMode = colorScheme === "dark";
  const [isUploading, setIsUploading] = React.useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCancelLogout = () => {
    if (loggingOut) {
      return;
    }

    setLogoutModalVisible(false);
  };

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      setLogoutModalVisible(false);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  const links = [
    {
      title: "Account Settings",
      items: [
        {
          title: "Edit Profile",
          icon: "person-outline",
          onPress: () => router.push("/profile/edit"),
          showSwitch: false,
          driverOnly: false,
        },
        {
          title: "Edit License",
          icon: "id-card-outline",
          onPress: () => router.push("/profile/license"),
          showSwitch: false,
          driverOnly: true,
        },
        {
          title: "Change Password",
          icon: "lock-closed-outline",
          onPress: () => router.push("/profile/change-password"),
          showSwitch: false,
          driverOnly: false,
        },
      ],
    },
    ...(isDriver
      ? [
          {
            title: "Email Preferences",
            items: [
              {
                title: "Job offer emails",
                icon: "mail-outline",
                onPress: () =>
                  handleEmailPreferenceToggle(
                    "job_offer_emails",
                    user?.job_offer_emails === false,
                  ),
                showSwitch: true,
                switchValue: user?.job_offer_emails !== false,
                driverOnly: true,
              },
              {
                title: "Account status emails",
                icon: "mail-unread-outline",
                onPress: () =>
                  handleEmailPreferenceToggle(
                    "account_update_emails",
                    user?.account_update_emails === false,
                  ),
                showSwitch: true,
                switchValue: user?.account_update_emails !== false,
                driverOnly: true,
              },
            ],
          },
        ]
      : []),
    {
      title: "Support & Legal",
      items: [
        {
          title: "Terms of Service",
          icon: "document-text-outline",
          onPress: () => router.push("/terms"),
          showSwitch: false,
          driverOnly: false,
        },
        {
          title: "Privacy Policy",
          icon: "shield-checkmark-outline",
          onPress: () => router.push("/privacy"),
          showSwitch: false,
          driverOnly: false,
        },
      ],
    },
    {
      title: "Settings",
      items: [
        {
          title: "Dark Mode",
          icon: "moon",
          onPress: () => handleThemeToggle(isDarkMode),
          showSwitch: true,
          driverOnly: false,
        },
        ...(isWeb
          ? []
          : [
              {
                title: "Check for Updates",
                icon: "download-outline",
                onPress: () => checkStoreUpdate(),
                showSwitch: false,
                driverOnly: false,
              },
            ]),
      ],
    },
    {
      title: "Session",
      items: [
        {
          title: "Logout",
          icon: "log-out-outline",
          onPress: () => setLogoutModalVisible(true),
          showSwitch: false,
          driverOnly: false,
        },
      ],
    },
    {
      title: "Account Deletion",
      items: [
        {
          title: "Delete Account",
          icon: "trash-outline",
          onPress: () => setDeleteModalVisible(true),
          showSwitch: false,
          driverOnly: false,
          isDestructive: true,
        },
      ],
    },
  ];

  const handleEmailPreferenceToggle = async (
    key: "job_offer_emails" | "account_update_emails",
    value: boolean,
  ) => {
    if (!user) {
      return;
    }

    const previousUser = user;
    setUser({ ...user, [key]: value });

    try {
      const response = await authService.updateEmailPreferences({
        [key]: value,
      });
      setUser(response.user);
    } catch (error) {
      console.error("Error updating email preferences:", error);
      setUser(previousUser);
      showToast.error(
        "Could not update your email preferences. Please try again.",
      );
    }
  };

  const handleThemeToggle = async (value: boolean) => {
    const nextIsDark = !value;
    setAppColorScheme(nextIsDark ? "dark" : "light");
  };

  const handleAvatarPress = async () => {
    const asset = await pickImageFromLibrary(
      imagePickerPresets.avatar,
      true,
      "Sorry, we need camera roll permissions to make this work!",
    );
    if (!asset) {
      return;
    }

    const normalizedAsset = await normalizeImageAsset(asset);
    const { isValid, message } = validateFileSize(normalizedAsset);
    if (!isValid) {
      showToast.error(
        message || "Failed to update profile picture. File size is too large.",
      );
      return;
    }

    try {
      setIsUploading(true);
      const { avatar } = await authService.updateAvatar(
        normalizedAsset.uri,
        normalizedAsset.mimeType || "image/jpeg",
        normalizedAsset.fileName || "avatar.jpg",
      );

      if (avatar && user) {
        setUser({ ...user, avatar });
        showToast.success("Profile picture updated successfully");
      }
    } catch (error) {
      showToast.error("Failed to update profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const fullName = `${user?.first_name} ${user?.last_name}`;

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      showAlert("Error", "Please enter your password to confirm.");
      return;
    }

    try {
      setIsDeleting(true);
      await authService.deleteAccount(deletePassword);
      setDeleteModalVisible(false);
      setDeletePassword("");
      showAlert("Success", "Your account has been deleted permanently.");
      logOut();
      router.replace("/(auth)");
    } catch (error: any) {
      showAlert(
        "Error",
        error.response?.data?.message ||
          "Failed to delete account. Please check your password.",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderMenuItem = (
    id: number,
    icon: keyof typeof Ionicons.glyphMap,
    label: string,
    onPress: () => void,
    showSwitch: boolean = false,
    isDestructive: boolean = false,
    switchValue?: boolean,
  ) => (
    <TouchableOpacity
      key={id}
      style={[
        styles.menuItem,
        { borderBottomWidth: 1, borderColor: theme.border },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={[styles.menuIconContainer]}>
        <Ionicons
          name={icon}
          size={22}
          color={isDestructive ? BrandColors.error : theme.text}
        />
      </View>
      <ThemedText
        style={[
          styles.menuLabel,
          isDestructive && { color: BrandColors.error },
        ]}
        type="defaultSemiBold"
      >
        {label}
      </ThemedText>
      {showSwitch ? (
        switchValue === undefined ? (
          <Switch
            value={isDarkMode}
            disabled={true}
            trackColor={{ false: "#767577", true: BrandColors.primary }}
          />
        ) : (
          <Switch
            value={switchValue}
            onValueChange={onPress}
            trackColor={{ false: "#767577", true: BrandColors.primary }}
          />
        )
      ) : (
        <Ionicons
          name="chevron-forward"
          size={20}
          color={isDestructive ? BrandColors.error : BrandColors.mediumGray}
        />
      )}
    </TouchableOpacity>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader title="Profile" />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.avatarContainer}
            onPress={handleAvatarPress}
            disabled={isUploading}
            activeOpacity={0.8}
          >
            <Avatar src={user?.avatar} name={fullName} size={120} />
            {isUploading ? (
              <View style={styles.uploadOverlay}>
                <ActivityIndicator color="#fff" size="small" />
              </View>
            ) : (
              <View style={styles.cameraIconContainer}>
                <Ionicons name="camera" size={16} color="#fff" />
              </View>
            )}
          </TouchableOpacity>
          <ThemedText style={styles.name} type="subtitle">
            {user ? `${user.first_name} ${user.last_name}` : ""}
          </ThemedText>
          <ThemedText style={styles.role} type="smallSemiBold">
            {getRoleLabel(user?.role)?.toUpperCase()}
          </ThemedText>
          <ThemedText style={styles.email} type="small">
            {user?.email}
          </ThemedText>
        </View>

        {links.map((section, index) => (
          <View style={styles.section} key={index}>
            <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
              {section.title}
            </ThemedText>
            <View
              style={[
                styles.menuCard,
                { backgroundColor: theme.background, shadowColor: theme.text },
              ]}
            >
              {section.items
                .filter((item: any) => !item.driverOnly || isDriver)
                .map((item: any, itemIndex) =>
                  renderMenuItem(
                    itemIndex,
                    item.icon,
                    item.title,
                    item.onPress,
                    item.showSwitch,
                    item.isDestructive,
                    item.switchValue,
                  ),
                )}
            </View>
          </View>
        ))}

        {!isWeb && (
          <ThemedText style={styles.version} type="extraSmall">
            Version {Application.nativeApplicationVersion || "1.0.0"}
          </ThemedText>
        )}
      </ScrollView>

      <LogOutModal
        visible={logoutModalVisible}
        onCancel={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        loading={loggingOut}
      />
      <Modal
        visible={deleteModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setDeleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[styles.modalContent, { backgroundColor: theme.background }]}
          >
            <View style={styles.modalHeader}>
              <Ionicons name="warning" size={32} color={BrandColors.error} />
              <ThemedText style={styles.modalTitle} type="subtitle">
                Delete Account
              </ThemedText>
            </View>
            <ThemedText style={styles.modalDescription}>
              This action is permanent and cannot be undone. All your data will
              be cleared.
            </ThemedText>
            <ThemedText style={styles.modalLabel}>
              Enter your password to confirm:
            </ThemedText>
            <Input
              placeholder="Password"
              value={deletePassword}
              onChangeText={setDeletePassword}
              secureTextEntry
              autoFocus
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setDeleteModalVisible(false);
                  setDeletePassword("");
                }}
                disabled={isDeleting}
              >
                <ThemedText style={styles.cancelButtonText}>Cancel</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.deleteButton,
                  isDeleting && styles.disabledButton,
                ]}
                onPress={handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <ThemedText style={styles.deleteButtonText}>
                    Delete
                  </ThemedText>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  header: {
    alignItems: "center",
    padding: 24,
    marginBottom: 24,
  },
  avatarContainer: {
    position: "relative",
    marginBottom: 16,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: BrandColors.primary,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    fontSize: 40,
    color: "#fff",
    fontWeight: "bold",
  },
  cameraIconContainer: {
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundColor: BrandColors.primary,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 3,
    borderColor: "#fff",
    zIndex: 1,
  },
  uploadOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
    borderRadius: 40,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
  },
  name: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 4,
  },
  role: {
    fontSize: 14,
    color: BrandColors.primary,
    fontWeight: "600",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  email: {
    fontSize: 14,
    color: BrandColors.mediumGray,
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 12,
    marginLeft: 4,
  },
  menuCard: {
    borderRadius: 16,
    overflow: "hidden",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  menuIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 16,
  },
  menuLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: "500",
  },
  divider: {
    height: 1,
    backgroundColor: BrandColors.lightGray,
    marginLeft: 72,
  },
  version: {
    textAlign: "center",
    color: BrandColors.mediumGray,
    fontSize: 12,
    marginTop: 24,
    marginBottom: 40,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    padding: 20,
  },
  modalContent: {
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  modalHeader: {
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    marginTop: 8,
    color: BrandColors.error,
  },
  modalDescription: {
    textAlign: "center",
    marginBottom: 20,
    color: BrandColors.mediumGray,
    lineHeight: 20,
  },
  modalLabel: {
    marginBottom: 8,
    fontSize: 14,
    fontWeight: "600",
  },
  modalButtons: {
    flexDirection: "row",
    marginTop: 24,
    gap: 12,
  },
  modalButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: BrandColors.lightGray,
  },
  deleteButton: {
    backgroundColor: BrandColors.error,
  },
  cancelButtonText: {
    color: BrandColors.darkGray,
    fontWeight: "600",
  },
  deleteButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  disabledButton: {
    opacity: 0.6,
  },
});
